import { computed } from 'vue'
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { Template } from '@/store/modules/template/templateTypes'

interface TemplatesComposable {
  // State y getters
  templateFields: ReturnType<typeof computed>
  templateTree: ReturnType<typeof computed>
  templateTreeEntityType: ReturnType<typeof computed>
  showTemplateGenerator: ReturnType<typeof computed>
  template: ReturnType<typeof computed>
  templateId: ReturnType<typeof computed>

  // Mutations
  resetTemplateTree: () => void
  setTreeEntityType: (idEntityType: number) => void

  // Actions
  fetchTemplateFields: (filter: any) => Promise<void>
  fetchTemplate: (id: number) => Promise<void>
  convertingToSFDT: (formData: FormData) => Promise<string | null>
  deleteTemplate: (id: number) => Promise<void>
  saveDocument: (formData: FormData) => Promise<void>
  saveTemplate: (template: Template) => Promise<void>
  saveTemplateAndDocument: (formData: FormData) => Promise<void>
  generateTemplate: (filter: any) => Promise<any>
  fetchTemplateTree: (filter: any) => Promise<any>
}

export default function useTemplates(): TemplatesComposable {
  const templatesModule = ModuleNamespaces.TEMPLATE

  // Getters
  const templateFields = computed(() => store.getters[`${templatesModule}/getTemplateFields`])
  const templateTree = computed(() => store.getters[`${templatesModule}/getTemplateTree`])
  const templateTreeEntityType = computed(() => store.getters[`${templatesModule}/getTemplateTreeEntityType`])
  const showTemplateGenerator = computed(() => store.getters[`${templatesModule}/showTemplateGenerator`])
  const template = computed(() => store.getters[`${templatesModule}/getTemplate`])
  const templateId = computed(() => store.getters[`${templatesModule}/getTemplateId`])

  // Mutations
  const resetTemplateTree = (): void => {
    store.commit(`${templatesModule}/RESET_TEMPLATE_TREE`)
  }

  const setTreeEntityType = (idEntityType: number): void => {
    store.commit(`${templatesModule}/SET_TEMPLATE_TREE_ENTITY_TYPE`, idEntityType)
  }

  // Actions
  const fetchTemplateFields = async (filter: any): Promise<void> => {
    return await store.dispatch(`${templatesModule}/fetchTemplateFields`, filter)
  }

  const fetchTemplate = async (id: number): Promise<void> => {
    return await store.dispatch(`${templatesModule}/fetchTemplate`, id)
  }

  const convertingToSFDT = async (formData: FormData): Promise<string | null> => {
    return await store.dispatch(`${templatesModule}/convertingToSFDT`, formData)
  }

  const deleteTemplate = async (id: number): Promise<void> => {
    return await store.dispatch(`${templatesModule}/deleteTemplate`, id)
  }

  const saveDocument = async (formData: FormData): Promise<void> => {
    return await store.dispatch(`${templatesModule}/saveDocument`, formData)
  }

  const saveTemplate = async (template: Template): Promise<void> => {
    return await store.dispatch(`${templatesModule}/saveTemplate`, template)
  }

  const saveTemplateAndDocument = async (formData: FormData): Promise<void> => {
    return await store.dispatch(`${templatesModule}/saveTemplateAndDocument`, formData)
  }

  const generateTemplate = async (filter: any): Promise<any> => {
    return await store.dispatch(`${templatesModule}/generateTemplate`, filter)
  }

  const fetchTemplateTree = async (filter: any): Promise<any> => {
    return await store.dispatch(`${templatesModule}/fetchTemplateTree`, filter)
  }

  return {
    // Getters
    templateFields,
    templateTree,
    templateTreeEntityType,
    showTemplateGenerator,
    template,
    templateId,

    // Mutations
    resetTemplateTree,
    setTreeEntityType,

    // Actions
    fetchTemplateFields,
    fetchTemplate,
    convertingToSFDT,
    deleteTemplate,
    saveDocument,
    saveTemplate,
    saveTemplateAndDocument,
    generateTemplate,
    fetchTemplateTree
  }
}
